import { defineStore } from "pinia";
import { animauxService } from "@/api/services/animauxService";

export const useAnimauxStore = defineStore("animaux", {
  state: () => ({
    animaux: [],
    animalTypes: [],
    animalRaces: [],
    allRaces: [],
    physiologicalStages: [],
    dailyActivities: [],
    weightManagements: [],
    loading: false,
    error: null,
    selectedProprietaireId: null,
  }),

  getters: {
    /**
     * Retourne les animaux formatés pour l'affichage dans un select
     */
    formattedAnimaux: state => {
      return state.animaux.map(animal => ({
        id: animal.id,
        name: animal.name,
        breed: animal.breed,
        type: animal.animalTypeId === 1 ? "Chien" : "Chat",
        birthDate: animal.birthDate,
        gender: animal.gender,
        weight: animal.weight,
        fullName: `${animal.name} (${animal.breed})`,
      }));
    },

    /**
     * Vérifie si des animaux sont disponibles
     */
    hasAnimaux: state => state.animaux.length > 0,

    /**
     * Vérifie si le chargement est en cours
     */
    isLoading: state => state.loading,

    /**
     * Retourne les races filtrées par type d'animal
     */
    getFilteredRaces: state => animalTypeId => {
      if (!animalTypeId) return [];
      return state.allRaces.filter(race => race.animalTypeId === parseInt(animalTypeId));
    },

    /**
     * Retourne les stades physiologiques filtrés par type d'animal
     */
    getFilteredPhysiologicalStages: state => animalTypeId => {
      if (!animalTypeId) return [];
      return state.physiologicalStages.filter(stage => stage.animalTypeId === parseInt(animalTypeId));
    },

    /**
     * Retourne le stade physiologique avec autoLoad = true pour un type d'animal donné
     */
    getAutoLoadPhysiologicalStage: state => animalTypeId => {
      if (!animalTypeId) return null;
      return state.physiologicalStages.find(
        stage => stage.animalTypeId === parseInt(animalTypeId) && stage.autoLoad === true
      );
    },

    /**
     * Retourne les activités quotidiennes filtrées par type d'animal
     */
    getFilteredDailyActivities: state => animalTypeId => {
      if (!animalTypeId) return [];
      return state.dailyActivities.filter(activity => activity.animalTypeId === parseInt(animalTypeId));
    },

    /**
     * Retourne les options de gestion de surpoids filtrées par type d'animal
     */
    getFilteredWeightManagements: state => animalTypeId => {
      if (!animalTypeId) return [];
      return state.weightManagements.filter(management => management.animalTypeId === parseInt(animalTypeId));
    },
  },

  actions: {
    /**
     * Récupère la liste des animaux d'un propriétaire
     * @param {number} proprietaireId - ID du propriétaire
     */
    async fetchAnimauxByProprietaire(proprietaireId) {
      if (!proprietaireId) {
        this.animaux = [];
        this.selectedProprietaireId = null;
        return;
      }

      this.loading = true;
      this.error = null;
      this.selectedProprietaireId = proprietaireId;

      try {
        const data = await animauxService.getAnimauxByProprietaire(proprietaireId);
        this.animaux = data;
      } catch (error) {
        this.error = "Erreur lors de la récupération des animaux";
        console.error("Erreur fetchAnimaux:", error);
        this.animaux = [];
      } finally {
        this.loading = false;
      }
    },

    /**
     * Récupère la liste des types d'animaux
     */
    async fetchAnimalTypes() {
      this.loading = true;
      this.error = null;

      try {
        const data = await animauxService.getAnimalTypes();
        this.animalTypes = data;
      } catch (error) {
        this.error = "Erreur lors de la récupération des types d'animaux";
        console.error("Erreur fetchAnimalTypes:", error);
      } finally {
        this.loading = false;
      }
    },

    /**
     * Récupère la liste des races d'animaux
     */
    async fetchAnimalRaces() {
      this.loading = true;
      this.error = null;

      try {
        const data = await animauxService.getAnimalRaces();
        this.allRaces = data;
        this.animalRaces = data;
      } catch (error) {
        this.error = "Erreur lors de la récupération des races d'animaux";
        console.error("Erreur fetchAnimalRaces:", error);
      } finally {
        this.loading = false;
      }
    },

    /**
     * Récupère la liste des stades physiologiques
     */
    async fetchPhysiologicalStages(animalTypeId = null) {
      this.loading = true;
      this.error = null;

      try {
        const data = await animauxService.getAnimalPhysiologicalStages(animalTypeId);
        this.physiologicalStages = data;
      } catch (error) {
        this.error = "Erreur lors de la récupération des stades physiologiques";
        console.error("Erreur fetchPhysiologicalStages:", error);
      } finally {
        this.loading = false;
      }
    },

    /**
     * Récupère la liste des activités quotidiennes
     */
    async fetchDailyActivities(animalTypeId = null) {
      this.loading = true;
      this.error = null;

      try {
        const data = await animauxService.getAnimalDailyActivities(animalTypeId);
        this.dailyActivities = data;
      } catch (error) {
        this.error = "Erreur lors de la récupération des activités quotidiennes";
        console.error("Erreur fetchDailyActivities:", error);
      } finally {
        this.loading = false;
      }
    },

    /**
     * Récupère la liste des options de gestion de surpoids
     */
    async fetchWeightManagements(animalTypeId = null) {
      this.loading = true;
      this.error = null;

      try {
        const data = await animauxService.getAnimalWeightManagements(animalTypeId);
        this.weightManagements = data;
      } catch (error) {
        this.error = "Erreur lors de la récupération des options de gestion de surpoids";
        console.error("Erreur fetchWeightManagements:", error);
      } finally {
        this.loading = false;
      }
    },

    /**
     * Ajoute un nouvel animal
     * @param {Object} animalData - Les données de l'animal à créer
     * @returns {Promise} Promesse contenant les données du nouvel animal
     */
    async addAnimal(animalData) {
      this.loading = true;
      this.error = null;

      try {
        const newAnimal = await animauxService.createAnimal(animalData);

        // Recharger la liste des animaux du propriétaire
        if (this.selectedProprietaireId) {
          await this.fetchAnimauxByProprietaire(this.selectedProprietaireId);
        }

        return newAnimal;
      } catch (error) {
        this.error = "Erreur lors de l'ajout de l'animal";
        console.error("Erreur addAnimal:", error);
        throw error;
      } finally {
        this.loading = false;
      }
    },

    /**
     * Met à jour un animal existant
     * @param {number} animalId - ID de l'animal à modifier
     * @param {Object} animalData - Les données de l'animal à mettre à jour
     * @returns {Promise} Promesse contenant les données de l'animal modifié
     */
    async updateAnimal(animalId, animalData) {
      this.loading = true;
      this.error = null;

      try {
        const updatedAnimal = await animauxService.updateAnimal(animalId, animalData);

        // Recharger la liste des animaux du propriétaire
        if (this.selectedProprietaireId) {
          await this.fetchAnimauxByProprietaire(this.selectedProprietaireId);
        }

        return updatedAnimal;
      } catch (error) {
        this.error = "Erreur lors de la modification de l'animal";
        console.error("Erreur updateAnimal:", error);
        throw error;
      } finally {
        this.loading = false;
      }
    },

    /**
     * Remet à zéro les états
     */
    resetAnimaux() {
      this.animaux = [];
      this.loading = false;
      this.error = null;
      this.selectedProprietaireId = null;
    },
  },
});
